import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { parse } from 'date-fns/parse'
import { startOfDay } from 'date-fns/startOfDay'
import { equals } from 'ramda'

import type { RecentHotelSearches } from '@/components/TripActivity/types'

/** A search older than this is treated as unrelated to the user's current trip. */
export const PREFILL_MAX_AGE_DAYS = 90

// The hotel (STAY) traveller selector's limits (`horizon-search-form`'s `HotelSearchForm`). A recent
// search can exceed them — results-page links can say anything — and occupancy the selector can't
// show or step must not be pre-filled.
const MAX_ROOMS = 9
const MAX_CHILDREN = 8
const MAX_TRAVELERS = 36
const MAX_CHILD_AGE = 17

/**
 * The longest stay the hotel form's calendar lets a user pick (`horizon-search-form`'s
 * `useHotelDateRangeCalendar` caps a range at 28 days from the check-in). A recent search can be
 * longer — other forms allow 30 nights, and results-page links can say anything — and a pre-filled
 * range the user couldn't have picked by hand must not be shown.
 */
export const MAX_STAY_NIGHTS = 28

export type PrefillTravelers = {
  adults: number
  children: number
  lapInfants: number
  childrenAges: Record<number, string>
  rooms: number
}

export type LastSearchPrefill = {
  startDate?: string
  endDate?: string
  travelers?: PrefillTravelers
}

const API_DATE_FORMAT = 'yyyy-MM-dd'
const FORM_DATE_FORMAT = 'yyyyMMdd'

function parseApiDate(value: string | undefined) {
  if (!value) return null
  const date = parse(value, API_DATE_FORMAT, new Date())
  return isValid(date) ? date : null
}

// Past, inverted or over-long dates would put the user on a search they never asked for, so they are
// dropped entirely rather than partially applied, and the form keeps its default dates.
function getDates({ checkInDate, checkOutDate }: RecentHotelSearches, today: Date) {
  const checkIn = parseApiDate(checkInDate)
  const checkOut = parseApiDate(checkOutDate)
  if (!checkIn || !checkOut || checkIn < today || checkOut <= checkIn) return null
  if (differenceInCalendarDays(checkOut, checkIn) > MAX_STAY_NIGHTS) return null
  return { startDate: format(checkIn, FORM_DATE_FORMAT), endDate: format(checkOut, FORM_DATE_FORMAT) }
}

// Children without a matching, valid age would trip the form's "child ages required" validation
// (or worse, submit a guess), so any inconsistency drops the whole traveller block.
function getTravelers({
  numberOfAdults,
  numberOfChildren,
  numberOfRooms,
  childAges: rawChildAges,
}: RecentHotelSearches): PrefillTravelers | null {
  // The API sends `null`, not an absent key, for a search with no children.
  const childAges = rawChildAges ?? []
  const rooms = numberOfRooms ?? 1
  const hasValidCounts =
    Number.isInteger(numberOfAdults) &&
    numberOfAdults >= 1 &&
    Number.isInteger(numberOfChildren) &&
    numberOfChildren >= 0 &&
    numberOfChildren <= MAX_CHILDREN &&
    numberOfAdults + numberOfChildren <= MAX_TRAVELERS &&
    Number.isInteger(rooms) &&
    rooms >= 1 &&
    rooms <= MAX_ROOMS
  if (!hasValidCounts || childAges.length !== numberOfChildren) return null

  const ages = childAges.map((age) => Number(age))
  if (
    ages.some((age, i) => childAges[i] === '' || !Number.isInteger(age) || age < 0 || age > MAX_CHILD_AGE)
  ) {
    return null
  }

  return {
    adults: numberOfAdults,
    children: numberOfChildren,
    lapInfants: 0,
    childrenAges: Object.fromEntries(childAges.map((age, i) => [i, String(Number(age))])),
    rooms,
  }
}

/**
 * Maps the user's most recent STAY search onto the hotel form's dates and travellers.
 * Returns null when nothing should be pre-filled; otherwise only the parts that passed validation.
 */
export function getPrefillFromLastSearch(
  search: RecentHotelSearches | null | undefined,
  now: Date = new Date(),
): LastSearchPrefill | null {
  if (search?.productType !== 'STAY') return null

  // Without a usable timestamp the 90-day window can't be checked, so don't guess.
  const searchedAt = search.creationDateTime ? new Date(search.creationDateTime) : null
  if (
    !searchedAt ||
    !isValid(searchedAt) ||
    differenceInCalendarDays(now, searchedAt) > PREFILL_MAX_AGE_DAYS
  ) {
    return null
  }

  const dates = getDates(search, startOfDay(now))
  const travelers = getTravelers(search)
  if (!dates && !travelers) return null

  return { ...dates, ...(travelers && { travelers }) }
}

/**
 * Whether two traveller values describe the same occupancy. The traveller selector reports its whole
 * state on open and close (label, setters, error message), so comparing the objects whole would call
 * an opened-and-closed selector "edited"; only the occupancy itself says whether the user changed it.
 */
export function isSameOccupancy(
  a: Partial<PrefillTravelers> | null | undefined,
  b: Partial<PrefillTravelers> | null | undefined,
) {
  const occupancy = (travelers: Partial<PrefillTravelers> | null | undefined) => ({
    adults: travelers?.adults ?? 0,
    children: travelers?.children ?? 0,
    lapInfants: travelers?.lapInfants ?? 0,
    rooms: travelers?.rooms ?? 0,
    childrenAges: travelers?.childrenAges ?? {},
  })
  return equals(occupancy(a), occupancy(b))
}

/** The part of a destination the autocomplete reports that identifies its city. */
export type DestinationWithCityId = { cityID?: string | number | null; id?: string | number | null }

export function getDestinationCityId(destination: DestinationWithCityId | null | undefined) {
  const cityId = destination?.cityID ?? destination?.id
  return cityId === undefined || cityId === null ? null : String(cityId)
}
